import { useMemo, useState } from 'react';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import { Panel, Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import { useCategoriasGasto, type ProyectoDetalle } from '../../lib/consultas';
import { useDefinirPresupuesto } from '../../lib/mutaciones';

/**
 * Presupuesto de costos por categoría.
 *
 * Es lo que convierte «llevo gastado tanto» en «me pasé en subcontratos y ahorré
 * en viáticos». Sin esto, la ficha solo puede comparar el gasto contra el monto
 * del contrato, que avisa tarde y no dice dónde se fue la plata.
 */
export function PestanaPresupuesto({ proyecto }: { proyecto: ProyectoDetalle }) {
  const avisos = useAvisos();
  const categorias = useCategoriasGasto();
  const definir = useDefinirPresupuesto();

  // Estado local del formulario: montos por categoría, sembrados con lo guardado.
  const [montos, setMontos] = useState<Record<string, number>>(() =>
    Object.fromEntries(proyecto.lineasPresupuesto.map((linea) => [linea.categoriaGastoId, linea.montoPlanificado])),
  );

  const gastoPorCategoria = useMemo(() => {
    const mapa: Record<string, number> = {};
    for (const gasto of proyecto.gastos) {
      mapa[gasto.categoriaGastoId] = (mapa[gasto.categoriaGastoId] ?? 0) + gasto.montoNeto;
    }
    return mapa;
  }, [proyecto.gastos]);

  const total = Object.values(montos).reduce((suma, monto) => suma + (monto || 0), 0);
  const margenResultante = proyecto.montoContratoNeto > 0 ? ((proyecto.montoContratoNeto - total) / proyecto.montoContratoNeto) * 100 : null;

  const guardar = async () => {
    const lineas = Object.entries(montos)
      .filter(([, monto]) => monto > 0)
      .map(([categoriaGastoId, montoPlanificado]) => ({ categoriaGastoId, montoPlanificado }));

    if (lineas.length === 0) {
      avisos.error('Define al menos una categoría con monto antes de guardar.');
      return;
    }

    try {
      await definir.mutateAsync({ proyectoId: proyecto.id, lineas });
      avisos.exito('Presupuesto de costos actualizado');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo guardar el presupuesto.');
    }
  };

  if (categorias.isLoading) return <Panel><Vacio titulo="Cargando categorías…" /></Panel>;

  return (
    <Panel
      titulo="Presupuesto de costos"
      subtitulo="Cuánto esperas gastar en cada categoría. Es la referencia contra la que se mide la desviación."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {categorias.data?.map((categoria) => {
          const planificado = montos[categoria.id] ?? 0;
          const real = gastoPorCategoria[categoria.id] ?? 0;
          const consumo = planificado > 0 ? (real / planificado) * 100 : null;

          return (
            <div
              key={categoria.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 180px 150px',
                gap: 14,
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div>
                <div style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{categoria.nombre}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{categoria.tipo}</div>
              </div>

              <input
                type="number"
                value={planificado || ''}
                onChange={(evento) => setMontos((previos) => ({ ...previos, [categoria.id]: Number(evento.target.value) }))}
                min={0}
                placeholder="0"
                aria-label={`Presupuesto para ${categoria.nombre}`}
                style={{
                  padding: '8px 12px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  textAlign: 'right',
                }}
              />

              <div style={{ fontSize: 11, textAlign: 'right' }}>
                {real > 0 ? (
                  <>
                    <div className="td-mono" style={{ color: 'var(--text-primary)' }}>{formatearCLP(real)}</div>
                    {consumo !== null && (
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          marginTop: 2,
                          color: consumo >= 100 ? 'var(--accent-red)' : consumo >= 80 ? 'var(--accent-yellow)' : 'var(--accent-green)',
                        }}
                      >
                        {formatearPorcentajeSimple(consumo)} usado
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>sin gastos</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Total presupuestado</div>
            <div className="td-mono" style={{ fontSize: 17, fontWeight: 700, marginTop: 3 }}>{formatearCLP(total)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Margen que dejaría</div>
            <div
              className="td-mono"
              style={{
                fontSize: 17,
                fontWeight: 700,
                marginTop: 3,
                color: margenResultante === null ? 'var(--text-muted)' : margenResultante >= 30 ? 'var(--accent-green)' : margenResultante >= 0 ? 'var(--accent-yellow)' : 'var(--accent-red)',
              }}
            >
              {margenResultante === null ? '—' : formatearPorcentajeSimple(margenResultante, 1)}
            </div>
          </div>
        </div>

        <button type="button" className="btn btn-primary" onClick={() => void guardar()} disabled={definir.isPending}>
          {definir.isPending ? 'Guardando…' : 'Guardar presupuesto'}
        </button>
      </div>

      {margenResultante !== null && margenResultante < 0 && (
        <div
          style={{
            marginTop: 14,
            padding: '11px 14px',
            background: 'var(--accent-red-soft)',
            color: 'var(--accent-red)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            lineHeight: 1.55,
          }}
        >
          Este presupuesto supera el monto del contrato: el proyecto daría pérdida incluso ejecutándose según lo
          planificado.
        </div>
      )}
    </Panel>
  );
}
